/**
 * FASE 3 v1.67 — Client Privacy Dashboard
 * Rendering della sezione "Privacy & GDPR" per l'Area Cliente drawer.
 *
 * Sezioni:
 *  - I tuoi consensi attivi (toggle GDPR granulari)
 *  - I tuoi diritti (6 card art. 15-21)
 *  - Storico consensi (immutabile)
 *  - Le tue richieste GDPR
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Eye, Edit3, Trash2, PauseCircle, Download, XOctagon, Info, CheckCircle2, Clock3, AlertTriangle, FileText } from "lucide-react";
import { PRIVACY_CONSENT_VERSION } from "@/config/legalMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client/privacy`;

// v1.68 (Fase 5) — testi snapshot dei consensi per il modale "Vedi cosa hai firmato"
const CONSENT_TEXT_SNAPSHOTS = {
  "2026-02": {
    contratto:             "Trattamento dati per elaborazione del preventivo assicurativo — base giuridica art. 6.1.b GDPR (misure precontrattuali).",
    contatto_commerciale:  "Contatto commerciale (email, telefono, WhatsApp) post-quotazione — art. 6.1.b GDPR.",
    trasmissione_compagnie:"Trasmissione dei dati alle compagnie assicurative partner per l'emissione dell'offerta — art. 6.1.b GDPR.",
    marketing:             "Newsletter Colucci Assicuratori — art. 6.1.a GDPR (consenso libero, revocabile).",
    profilazione:          "Profilazione per suggerimenti personalizzati — art. 6.1.a + 22 GDPR.",
    dati_sanitari:         "Trattamento dati sanitari — art. 9.2.a GDPR (consenso esplicito). Cifratura AES-256-GCM at-rest.",
    gruppo_partner:        "Comunicazione a società partner del gruppo — art. 6.1.f GDPR (legittimo interesse).",
  },
};

export default function PrivacyDashboard({ getToken, onLogout }) {
  const [consents, setConsents] = useState([]);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [versionCheck, setVersionCheck] = useState(null); // v1.68 Fase 5
  const [showSignedText, setShowSignedText] = useState(null); // finalita per modal
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // finalita in fase di toggle
  const [showDelete, setShowDelete] = useState(false);
  const [showRectify, setShowRectify] = useState(false);
  const [rectifyField, setRectifyField] = useState("");
  const [rectifyValue, setRectifyValue] = useState("");
  const [deletePwd, setDeletePwd] = useState("");

  const token = getToken?.();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const reload = async () => {
    setLoading(true);
    try {
      const [c, h, r, v] = await Promise.all([
        axios.get(`${API}/consents`, { headers }),
        axios.get(`${API}/history`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/requests`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/version-check`, { headers }).catch(() => ({ data: null })),
      ]);
      setConsents(c.data?.items || []);
      setHistory(h.data?.items || []);
      setRequests(r.data?.items || []);
      setVersionCheck(v.data || null);
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore caricamento dashboard privacy";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggle = async (finalita, currentGranted) => {
    setBusy(finalita);
    try {
      await axios.post(
        `${API}/consents/${encodeURIComponent(finalita)}/toggle`,
        { granted: !currentGranted },
        { headers }
      );
      toast.success(!currentGranted ? "Consenso concesso" : "Consenso revocato");
      await reload();
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Impossibile aggiornare il consenso";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const requestAccess = async () => {
    try {
      const { data } = await axios.get(`${API}/summary`, { headers });
      const total = Object.values(data.richieste_count_by_ramo || {}).reduce((a, b) => a + b, 0);
      toast.success(`Dati recuperati: ${total} richieste totali, ${data.active_sessions} sessioni attive.`);
    } catch (e) {
      toast.error("Errore recupero dati");
    }
  };

  const requestRectify = async () => {
    if (!rectifyField || !rectifyValue) {
      toast.error("Compila entrambi i campi");
      return;
    }
    try {
      await axios.post(`${API}/rectify`,
        { field: rectifyField, new_value: rectifyValue },
        { headers });
      toast.success("Richiesta di rettifica inviata al DPO");
      setShowRectify(false); setRectifyField(""); setRectifyValue("");
      await reload();
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore invio richiesta rettifica";
      toast.error(msg);
    }
  };

  const requestDelete = async () => {
    if (!deletePwd) { toast.error("Inserisci la password per confermare"); return; }
    try {
      await axios.post(`${API}/delete-account`, { password: deletePwd }, { headers });
      toast.success("Richiesta di cancellazione registrata. Sarai disconnesso.");
      setShowDelete(false);
      setTimeout(() => onLogout?.(), 2000);
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore richiesta cancellazione";
      toast.error(msg);
    }
  };

  const requestRestrict = async () => {
    try {
      await axios.post(`${API}/restrict-processing`, {}, { headers });
      toast.success("Limitazione trattamento richiesta");
      await reload();
    } catch (e) {
      toast.error("Errore richiesta limitazione");
    }
  };

  const requestExport = async () => {
    try {
      await axios.post(`${API}/export-request`, {}, { headers });
      toast.success("Richiesta di export dati in coda. Ti avviseremo quando pronta.");
      await reload();
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore invio richiesta export";
      toast.error(msg);
    }
  };

  if (loading) {
    return <p className="text-sm text-zinc-500 text-center py-12">Caricamento privacy dashboard...</p>;
  }

  return (
    <div className="space-y-6" data-testid="privacy-dashboard-root">
      {/* v1.68 Fase 5 — Banner re-consent se versione obsoleta */}
      {versionCheck?.needs_update && (
        <section
          data-testid="privacy-reconsent-banner"
          className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              L&apos;informativa privacy è stata aggiornata
            </p>
            <p className="text-[11.5px] text-amber-800 mt-1 leading-relaxed">
              Versione firmata: <strong>v{versionCheck.user_last_version}</strong> ·
              Versione attuale: <strong>v{versionCheck.current_version}</strong>.
              Per continuare a ricevere le nostre comunicazioni, aggiorna i tuoi consensi.
            </p>
            <button
              type="button"
              onClick={() => document.getElementById("privacy-consents-anchor")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="privacy-reconsent-cta"
              className="mt-2 text-xs bg-amber-700 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-amber-800"
            >
              Rivedi consensi
            </button>
          </div>
        </section>
      )}
      {/* Intro */}
      <section className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4">
        <div className="flex items-start gap-2.5">
          <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-[#0F1F14] text-sm">Privacy e i tuoi diritti GDPR</h3>
            <p className="text-xs text-zinc-600 leading-relaxed mt-1">
              Qui puoi vedere e gestire tutti i consensi che hai dato a Colucci Assicuratori,
              esercitare i tuoi diritti GDPR e scaricare i tuoi dati.
            </p>
          </div>
        </div>
      </section>

      {/* Consensi attivi */}
      <section data-testid="privacy-consents-section" id="privacy-consents-anchor">
        <h4 className="text-sm font-semibold text-[#163321] mb-2 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" /> I tuoi consensi attivi
        </h4>
        <ul className="space-y-2">
          {consents.map((c) => {
            const isStale = c.consent_text_version && c.consent_text_version < PRIVACY_CONSENT_VERSION;
            return (
              <li key={c.finalita}
                  data-testid={`privacy-consent-${c.finalita}`}
                  className="bg-white border border-zinc-200 rounded-xl p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 leading-snug">{c.label}</p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span className="text-[11px] text-zinc-500">
                      {c.obbligatorio ? "Obbligatorio" : "Opzionale"} · art. {c.base_giuridica}
                    </span>
                    {c.consent_text_version && (
                      <span
                        data-testid={`privacy-consent-version-${c.finalita}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          isStale ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600"
                        }`}
                      >
                        v{c.consent_text_version}{isStale && " · da aggiornare"}
                      </span>
                    )}
                    {c.consent_text_version && (
                      <button
                        type="button"
                        onClick={() => setShowSignedText(c.finalita)}
                        data-testid={`privacy-view-signed-${c.finalita}`}
                        className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-0.5"
                      >
                        <FileText className="w-3 h-3" /> Vedi cosa hai firmato
                      </button>
                    )}
                    {c.last_change && (
                      <span className="text-[10px] text-zinc-400">
                        agg. {String(c.last_change).slice(0, 10)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={c.obbligatorio || busy === c.finalita}
                  onClick={() => toggle(c.finalita, c.granted)}
                  title={c.obbligatorio ? "Consenso obbligatorio per il servizio. Per revocarlo, cancella l'account." : ""}
                  data-testid={`privacy-toggle-${c.finalita}`}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition ${
                    c.granted ? "bg-emerald-600" : "bg-zinc-300"
                  } ${c.obbligatorio ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition ${
                    c.granted ? "left-5" : "left-0.5"
                  }`} />
                </button>
              </li>
            );
          })}
          {consents.length === 0 && (
            <li className="text-xs text-zinc-400 text-center py-4">
              Nessun consenso registrato. Compila un preventivo per iniziare.
            </li>
          )}
        </ul>
      </section>

      {/* Diritti GDPR */}
      <section data-testid="privacy-rights-section">
        <h4 className="text-sm font-semibold text-[#163321] mb-2 flex items-center gap-1.5">
          <Info className="w-4 h-4 text-emerald-700" /> I tuoi diritti
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <RightCard icon={Eye} title="Accesso" art="art. 15"
            desc="Vedi tutti i tuoi dati" onClick={requestAccess}
            testid="privacy-right-access" />
          <RightCard icon={Edit3} title="Rettifica" art="art. 16"
            desc="Correggi dati errati" onClick={() => setShowRectify(true)}
            testid="privacy-right-rectify" />
          <RightCard icon={Trash2} title="Cancellazione" art="art. 17"
            desc="Cancella il tuo account" onClick={() => setShowDelete(true)}
            danger testid="privacy-right-delete" />
          <RightCard icon={PauseCircle} title="Limitazione" art="art. 18"
            desc="Sospendi trattamento" onClick={requestRestrict}
            testid="privacy-right-restrict" />
          <RightCard icon={Download} title="Portabilità" art="art. 20"
            desc="Esporta i tuoi dati (ZIP)" onClick={requestExport}
            testid="privacy-right-export" />
          <RightCard icon={XOctagon} title="Opposizione" art="art. 21"
            desc="Revoca marketing/profilazione (usa i toggle sopra)"
            onClick={() => toast.info("Usa i toggle 'Marketing' e 'Profilazione' sopra per opporti al trattamento.")}
            testid="privacy-right-oppose" />
        </div>
      </section>

      {/* Richieste GDPR */}
      <section data-testid="privacy-requests-section">
        <h4 className="text-sm font-semibold text-[#163321] mb-2 flex items-center gap-1.5">
          <Clock3 className="w-4 h-4 text-emerald-700" /> Le tue richieste GDPR
        </h4>
        {requests.length === 0 ? (
          <p className="text-xs text-zinc-400 text-center py-4">Nessuna richiesta attiva.</p>
        ) : (
          <ul className="space-y-1.5">
            {requests.map((r) => (
              <li key={r.id} className="text-xs bg-white border border-zinc-200 rounded-lg p-2.5 flex justify-between">
                <span className="font-medium text-zinc-800">{r.type}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                  r.status === "completed" ? "bg-emerald-100 text-emerald-800"
                    : r.status === "rejected" ? "bg-rose-100 text-rose-800"
                    : "bg-amber-100 text-amber-800"
                }`}>{r.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Storico consensi */}
      <section data-testid="privacy-history-section">
        <details className="bg-zinc-50 rounded-xl border border-zinc-100">
          <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-[#163321]">
            Storico consensi ({history.length})
          </summary>
          <ul className="px-3 pb-3 space-y-1 max-h-48 overflow-y-auto">
            {history.map((h, idx) => (
              <li key={idx} className="text-[11px] text-zinc-600 border-b border-zinc-200/60 last:border-0 py-1.5">
                <span className={h.granted ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                  {h.granted ? "✓ grant" : "✗ revoke"}
                </span>{" "}
                <span className="text-zinc-800">{h.finalita}</span>
                {" · "}<span className="text-zinc-500">{h.wizard_source || "—"}</span>
                {" · "}<span className="text-zinc-400">{String(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
            {history.length === 0 && (
              <li className="text-xs text-zinc-400 text-center py-3">Nessun evento registrato.</li>
            )}
          </ul>
        </details>
      </section>

      {/* Modali */}
      {showRectify && (
        <Modal onClose={() => setShowRectify(false)} title="Richiedi rettifica dati">
          <label className="text-xs text-zinc-600 font-medium">Campo da correggere</label>
          <input value={rectifyField} onChange={(e) => setRectifyField(e.target.value)}
                 placeholder="es. telefono, indirizzo..."
                 data-testid="privacy-rectify-field"
                 className="w-full mt-1 mb-3 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          <label className="text-xs text-zinc-600 font-medium">Nuovo valore</label>
          <input value={rectifyValue} onChange={(e) => setRectifyValue(e.target.value)}
                 data-testid="privacy-rectify-value"
                 className="w-full mt-1 mb-4 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          <button onClick={requestRectify}
                  data-testid="privacy-rectify-submit"
                  className="w-full bg-emerald-700 text-white rounded-lg py-2 text-sm font-medium hover:bg-emerald-800">
            Invia richiesta
          </button>
        </Modal>
      )}

      {showDelete && (
        <Modal onClose={() => setShowDelete(false)} title="Cancellazione account">
          <p className="text-xs text-zinc-700 leading-relaxed mb-3">
            <strong>Attenzione</strong>: la cancellazione è irreversibile per i dati non sotto obbligo IVASS.
            I dati soggetti a retention legale (10 anni ex Reg. IVASS 44/2019) saranno mantenuti fino a scadenza,
            poi eliminati automaticamente.
          </p>
          <label className="text-xs text-zinc-600 font-medium">Conferma con la tua password</label>
          <input type="password" value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)}
                 data-testid="privacy-delete-password"
                 className="w-full mt-1 mb-4 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          <button onClick={requestDelete}
                  data-testid="privacy-delete-submit"
                  className="w-full bg-rose-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-rose-700">
            Conferma cancellazione
          </button>
        </Modal>
      )}

      {/* v1.68 Fase 5 — Modal "Vedi cosa hai firmato" */}
      {showSignedText && (() => {
        const c = consents.find((x) => x.finalita === showSignedText);
        const version = c?.consent_text_version || PRIVACY_CONSENT_VERSION;
        const text = CONSENT_TEXT_SNAPSHOTS[version]?.[showSignedText]
          || CONSENT_TEXT_SNAPSHOTS[PRIVACY_CONSENT_VERSION]?.[showSignedText]
          || "Testo consenso non disponibile per questa versione.";
        return (
          <Modal onClose={() => setShowSignedText(null)} title="Testo del consenso firmato">
            <div data-testid="privacy-signed-text-modal">
              <p className="text-[11px] text-zinc-500 mb-2">
                Versione firmata: <strong>v{version}</strong>
                {version < PRIVACY_CONSENT_VERSION && (
                  <span className="ml-2 text-amber-700">
                    (versione corrente: v{PRIVACY_CONSENT_VERSION})
                  </span>
                )}
              </p>
              <p className="text-sm text-zinc-800 leading-relaxed border-l-2 border-emerald-500 pl-3 py-1 bg-emerald-50/40 rounded">
                {text}
              </p>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

function RightCard({ icon: Icon, title, art, desc, onClick, danger = false, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border p-3 transition ${
        danger
          ? "border-rose-200 bg-rose-50/40 hover:bg-rose-50"
          : "border-zinc-200 bg-white hover:bg-zinc-50"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${danger ? "text-rose-700" : "text-emerald-700"}`} />
        <p className="text-sm font-semibold text-zinc-900">{title}</p>
        <span className="text-[10px] text-zinc-400 ml-auto">{art}</span>
      </div>
      <p className="text-[11px] text-zinc-600 leading-snug">{desc}</p>
    </button>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="privacy-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
           onClick={(e) => e.stopPropagation()}>
        <h5 className="text-base font-semibold text-[#163321] mb-3">{title}</h5>
        {children}
        <button onClick={onClose}
                className="w-full mt-2 text-xs text-zinc-500 hover:text-zinc-800">
          Annulla
        </button>
      </div>
    </div>
  );
}
